import { useMemo } from 'react';

import { useGetVideoVersionAudiotracks } from '@vimeo/gctl-api/hooks/useVideoVersionAudiotracks';
import type {
  AlternateAudioTrack,
  AlternateAudioTrackFields,
} from '@vimeo/gctl-api/models/components/schemas/alternate-audio-track/AlternateAudioTrack';
import type { VideoTranslation } from '@vimeo/gctl-api/models/components/schemas/video-translation/VideoTranslation';
import { usePoll } from '@vimeo/ui/hooks/use-poll';

import type { TAudioTrack } from '../types';
import { TRACKED_ERRORS, trackError } from '../utils/datadog';
import { mapVideoIdTypeForGCTL } from '../utils/mapVideoIdTypeForGCTL';

const AUDIO_STATUSES = {
  COMPLETE: 'complete',
  ERROR: 'error',
  IN_PROGRESS: 'in_progress',
};

const POLLING_INTERVAL = 10_000;

const TRACK_LOADING_STATUS = 'PRCS';

const AUDIO_TRACK_FIELDS: AlternateAudioTrackFields[] = [
  'language',
  'transcode',
  'type',
  'upload',
  'uri',
  'quota',
  'provenance',
  'active',
  'download',
];

type Props = {
  videoId?: string;
  videoVersionId?: number;
  keepPollingTranslations?: boolean;
  translations?: VideoTranslation[];
  exclusiveAccess: boolean;
};
export function useClipAudioTracks({
  videoId,
  videoVersionId,
  keepPollingTranslations,
  translations,
  exclusiveAccess,
}: Props) {
  const SWRHandle = useGetVideoVersionAudiotracks(
    () => {
      if (!videoVersionId || !videoId) {
        return null;
      }
      return {
        where: {
          ...mapVideoIdTypeForGCTL(videoId),
          versionId: videoVersionId,
        },
        select: AUDIO_TRACK_FIELDS,
        headers: { Accept: 'application/vnd.vimeo.*+json;version=3.4.10' },
      };
    },
    {
      refreshWhenHidden: false,
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    },
  );
  const {
    error: audioTracksError,
    mutate: getAudioTracks,
    data,
    isLoading,
    isValidating,
  } = SWRHandle;

  const audioTracksResponseData = data as unknown as { data: AlternateAudioTrack[] } | undefined;

  const alternateAudioTracks = audioTracksResponseData?.data;

  const hasValidResponse = alternateAudioTracks?.length !== undefined;

  const keepPollingAudio = Boolean(
    alternateAudioTracks?.some((track) => track.transcode?.status === AUDIO_STATUSES.IN_PROGRESS),
  );

  usePoll(getAudioTracks, keepPollingAudio || keepPollingTranslations, {
    interval: POLLING_INTERVAL,
    backoff: 'exponential',
    shouldResetBackoff: (requestCount: number) => requestCount > 5,
  });

  const accessibleAudioTracks = useMemo(() => {
    return (alternateAudioTracks ?? [])
      .filter((track) => {
        return exclusiveAccess || track.active;
      })
      .map((track: AlternateAudioTrack): TAudioTrack => {
        const { code } = track.language;

        const hasTranslationInProgress = translations?.find(
          (translation: VideoTranslation) =>
            translation.targetLang === code && translation.status === TRACK_LOADING_STATUS,
        );

        // we skip those tracks which being transcoded
        const isTranscoding = track.transcode?.status === AUDIO_STATUSES.IN_PROGRESS;
        // we skip those tracks which being uploaded
        const isUploading = track.upload?.status === AUDIO_STATUSES.IN_PROGRESS;

        if (track.upload === undefined) {
          // this is unexpected yet should be monitored
          trackError(new Error('Missing "upload" property in audio track.'), {
            uri: track.uri,
            method: 'useClipAudioTracks',
            trackingTag: TRACKED_ERRORS.AUDIOTRACK_MISSING_UPLOAD,
          });
        }

        const disableReason = isUploading
          ? 'uploading'
          : isTranscoding
          ? 'transcoding'
          : hasTranslationInProgress
          ? 'beingTranslated'
          : undefined;

        return {
          ...track,
          disableReason,
        };
      });
  }, [alternateAudioTracks, exclusiveAccess, translations]);

  return {
    alternateAudioTracks,
    accessibleAudioTracks,
    hasValidResponse,
    audioTracksError,
    getAudioTracks,
    isLoading,
    isValidating,
  };
}
